import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { Btn } from "../components/ui-kit";
import { api } from "../lib/api";
import { useMember } from "../lib/memberAuth";
import { SocialButtons } from "../components/account/SocialButtons";

const GivingSignupPrompt = () => {
  const { member } = useMember();
  if (member) return null;
  return (
    <div className="mt-10 bg-[#FAFAFA] border border-black/10 rounded-2xl p-6 text-left" data-testid="giving-signup-prompt">
      <p className="font-display font-bold text-center">Track your giving</p>
      <p className="text-sm text-black/50 mt-1 mb-4 text-center">Create a free One Love account to see your giving history and contribution statements anytime.</p>
      <SocialButtons intent="login" />
      <Link to="/account/login" className="block text-center text-sm text-[#E63946] font-medium mt-3 hover:underline">Create Account with Email</Link>
    </div>
  );
};

export default function GiveSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/stripe/status/${sessionId}`);
        setInfo(data.record || {});
        if (data.paid) { setStatus("paid"); return; }
        if (["expired", "canceled"].includes(data.status)) { setStatus("error"); return; }
      } catch (e) { console.warn("Payment status check failed, retrying…", e); }
      attempts += 1;
      if (attempts < 8) setTimeout(poll, 2000);
      else setStatus("pending");
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen grid place-items-center bg-white pt-[72px] px-5" data-testid="give-success-page">
      <div className="text-center max-w-md">
        {status === "checking" && <><Loader2 className="animate-spin mx-auto text-[#E63946] mb-6" size={48} /><h1 className="font-display font-bold text-3xl">Confirming your gift…</h1></>}
        {status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-[#E63946] mb-6" size={56} />
            {info?.kind === "ticket" ? (
              <>
                <h1 className="font-display font-extrabold text-4xl mb-4">You're going!</h1>
                <p className="text-black/60 text-lg mb-8">Your purchase of <span className="font-bold text-black">{info?.quantity} ticket{Number(info?.quantity) > 1 ? "s" : ""}</span> to <span className="font-bold text-black">{info?.event_title}</span> is confirmed. We've emailed your receipt and a QR code to show at the door. We can't wait to celebrate with you!</p>
                <Btn to="/events" testid="success-events">Back to Events</Btn>
              </>
            ) : (
              <>
                <h1 className="font-display font-extrabold text-4xl mb-4">Thank you!</h1>
                <p className="text-black/60 text-lg mb-8">Your {info?.recurring ? `${info?.frequency} ` : ""}gift of <span className="font-bold text-black">${info?.amount}</span> to {info?.fund} has been received. {info?.recurring ? "Your recurring gift is now active. " : ""}Your generosity is a blessing to our church family and city.</p>
                <Btn to="/" testid="success-home">Back to Home</Btn>
                <GivingSignupPrompt />
              </>
            )}
          </>
        )}
        {status === "pending" && <><Loader2 className="animate-spin mx-auto text-[#E63946] mb-6" size={48} /><h1 className="font-display font-bold text-2xl mb-3">Still processing</h1><p className="text-black/60 mb-6">Your payment is being confirmed. You'll receive a confirmation shortly.</p><Btn to="/" testid="success-home">Back to Home</Btn></>}
        {status === "error" && <><XCircle className="mx-auto text-black/40 mb-6" size={56} /><h1 className="font-display font-bold text-2xl mb-3">Something went wrong</h1><p className="text-black/60 mb-6">We couldn't confirm your gift. Please try again or contact us.</p><Btn to="/give" testid="success-retry">Return to Giving</Btn></>}
      </div>
    </div>
  );
}
